import { isArray } from 'lodash';
import { useEffect, useState } from 'react';
import { generatePath, useParams } from 'react-router-dom';
import { AlertType, useAlert } from 'src/components/AlertsProvider';
import ModalDialog, {
  ModalCloseHandler,
  ModalCloseReason,
} from 'src/components/ModalDialog';
import { useStoreUnitActions } from 'src/redux/helpers';
import { pepApiRequest, RequestMethod, useStatefulApi } from 'src/services';
import { useForm } from 'src/components/Form/hooks';
import { Option } from 'src/components/SelectOption';
import solutionsStore, { useSolutionDetails } from '../../redux';
import {
  API_ALL_SOURCE_DOCKER_IMAGES,
  API_SOURCE_DOCKER_IMAGES,
} from '../../constants';
import { SourcePullImageFormFields } from './formFields';
import {
  SolutionSourceImageRepoContainerType,
  SolutionSourceImageRepositoryItem,
} from '../../types';

type Props = {
  item?: SolutionSourceImageRepositoryItem;
  open: boolean;
  onClose: ModalCloseHandler;
};

function SolutionSourcePullImageConfig({ item, open, onClose }: Props) {
  const { showAlert } = useAlert();
  const [data] = useSolutionDetails();
  const { solutionId } = useParams();
  const actions = useStoreUnitActions(solutionsStore);
  const [repoNames, setRepoNames] = useState([]);
  const [selectedRepoName, setSelectedRepoName] = useState('');
  const [imagesOptions, setOptions] = useState<Option[]>([]);
  const [loadingImages, setImageLoading] = useState<boolean>(false);
  const [onSubmit, loading] = useStatefulApi(
    (params) =>
      pepApiRequest({
        method: RequestMethod.Post,
        url: generatePath(API_SOURCE_DOCKER_IMAGES, { solutionId }),
        params,
      }),
    () => {
      showAlert({
        type: AlertType.Info,
        title: 'Images are pulled',
        text: 'Selected images are pulled successfully',
      });
      onClose(null, ModalCloseReason.completeAction);
      actions.details(solutionId);
    }
  );
  const [canSelectImage, setCanSelectImage] = useState(true);

  const getDockerImages = (repoName) => {
    setImageLoading(true);
    pepApiRequest({
      method: RequestMethod.Get,
      url: generatePath(API_ALL_SOURCE_DOCKER_IMAGES, {
        solutionId,
        sourceImageRepoName: repoName,
      }),
    }).then((images) => {
      if (!isArray(images)) {
        setImageLoading(false);
        return;
      }
      const imagesOptions = images.map((imageName) => {
        const option: Option = {
          label: imageName,
          value: imageName,
        };
        return option;
      });
      setOptions(imagesOptions);
      setImageLoading(false);
    });
  };

  useEffect(() => {
    if (!item || !selectedRepoName) return;
    const validClientDetails = item?.sourceImageRepo?.filter(
      (repo) => repo.sourceImageRepoName === selectedRepoName
    );
    if (
      validClientDetails[0]?.containerRepositoryType ===
      SolutionSourceImageRepoContainerType.Ecr
    ) {
      setCanSelectImage(false);
    } else if (
      validClientDetails[0]?.acrClientId === '' ||
      validClientDetails[0]?.acrClientId === undefined ||
      validClientDetails[0]?.acrClientSecretName === '' ||
      validClientDetails[0]?.acrClientSecretName === undefined
    ) {
      setCanSelectImage(false);
    } else {
      setCanSelectImage(true);
      getDockerImages(selectedRepoName);
    }
  }, [selectedRepoName]);

  useEffect(() => {
    const repoName = data?.industrialWorkloads?.sourceImageRepo
      ?.filter((i) => i.sourceImageRepoName && i.sourceImageRepoName !== '')
      .map((item) => {
        const option: Option = {
          label: item?.sourceImageRepoName,
          value: item?.sourceImageRepoName,
        };
        return option;
      });
    setRepoNames(repoName);
  }, [data]);

  const handleChange = (event) => {
    setSelectedRepoName(event.target.value);
  };

  const [form, handleSubmit] = useForm({
    fields: SourcePullImageFormFields(
      repoNames,
      imagesOptions,
      canSelectImage,
      handleChange
    ),
    onSubmit,
  });

  return (
    <ModalDialog
      open={open}
      title="Pull docker images"
      loading={loading || loadingImages}
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmBtnText="Submit"
      maxWidth="sm"
      fullWidth
    >
      {form}
    </ModalDialog>
  );
}

export default SolutionSourcePullImageConfig;
